<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->


<template>
<div :class="[layoutTypeClass, navbarClasses, footerClasses, {'no-scroll': isAppPage}]" class="layout--main">

  <v-nav-menu
    :navMenuItems="navMenuItems"
    parent=".layout--main"
    title="Vuexy"/>

  <div :class="[contentAreaClass, {'show-overlay': bodyOverlay}]" id="content-area">
    <div id="content-overlay"/>

    <!-- Navbar -->
    <template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
    <the-navbar-horizontal
      :class="[
          {'text-white' : isNavbarDark  && !isThemeDark},
          {'text-base'  : !isNavbarDark && isThemeDark}
        ]"
      :navbarType="navbarType"/>

    <div style="height: 62px" v-if="navbarType === 'static'"></div>

    <h-nav-menu
      :class="[
          {'text-white' : isNavbarDark  && !isThemeDark},
          {'text-base'  : !isNavbarDark && isThemeDark}
        ]"
      :navMenuItems="navMenuItems"/>
    </template>

    <template v-else>
    <the-navbar-vertical
      :class="[
          {'text-white' : isNavbarDark  && !isThemeDark},
          {'text-base'  : !isNavbarDark && isThemeDark}
        ]"
      :navbarColor="navbarColor"/>
    </template>
    <!-- /Navbar -->

    <div class="content-wrapper">

      <div class="router-view">
        <div class="router-content">

          <maintenance-bar v-if="maintenance"/>
          <transition :name="routerTransition">

            <div class="router-header flex flex-wrap items-center mb-6" v-if="$route.meta.breadcrumb || $route.meta.pageTitle">
              <div
                :class="{'pr-4 border-0 md:border-r border-solid border-grey-light' : $route.meta.breadcrumb}"
                class="content-area__heading">
                <h2 class="mb-1">{{ $t(routeTitle, routeTitleParams) }}</h2>
              </div>

              <!-- BREADCRUMB -->
              <vx-breadcrumb :isRTL="$vs.rtl" :route="$route" class="ml-4 md:block hidden" v-if="$route.meta.breadcrumb"/>

              <!-- DROPDOWN -->
              <vs-dropdown class="ml-auto md:block hidden cursor-pointer" vs-trigger-click v-if="filteredGlobalAction.length > 0">
                <vs-button icon="icon-menu" icon-pack="feather" radius/>

                <vs-dropdown-menu class="w-32">
                  <vs-dropdown-item v-for="(action, index) in filteredGlobalAction" :key="index">
                    <div @click="$router.push(action.route)" class="flex items-center" v-if="!action.execute">
                      <feather-icon class="inline-block mr-2" :icon="action.icon" svgClasses="w-4 h-4"/>
                      <span>{{action.title}}</span>
                    </div>

                    <div @click="action.execute" class="flex items-center" v-else>
                      <feather-icon class="inline-block mr-2" :icon="action.icon" svgClasses="w-4 h-4"/>
                      <span>{{action.title}}</span>
                    </div>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>

            </div>
          </transition>

          <div class="content-area__content">

            <back-to-top :right="$vs.rtl ? 'calc(100% - 2.2rem - 38px)' : '30px'" bottom="5%" v-if="!hideScrollToTop" visibleoffset="500">
              <vs-button class="shadow-lg btn-back-to-top" icon="icon-arrow-up" icon-pack="feather"/>
            </back-to-top>

            <transition :name="routerTransition" mode="out-in">
              <router-view @changeRouteTitle="changeRouteTitle" @setAppClasses="(classesStr) => $emit('setAppClasses', classesStr)"/>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <the-footer/>
  </div>
</div>
</template>


<script>
import BackToTop from 'vue-backtotop';
import HNavMenu from '@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue';
import navMenuItems from '@/layouts/components/vertical-nav-menu/navMenuItems.js';
import TheNavbarHorizontal from '@/layouts/components/navbar/TheNavbarHorizontal.vue';
import TheNavbarVertical from '@/layouts/components/navbar/TheNavbarVertical.vue';
import TheFooter from '@/layouts/components/TheFooter.vue';
import themeConfig from '@/../themeConfig.js';
import VNavMenu from '@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue';
import MaintenanceBar from '@/layouts/components/navbar/components/maintenance-bar';

export default {
  components: {
    MaintenanceBar,
    BackToTop,
    HNavMenu,
    TheFooter,
    TheNavbarHorizontal,
    TheNavbarVertical,
    VNavMenu
  },
  data () {
    return {
      footerType: themeConfig.footerType || 'static',
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || '#fff',
      navbarType: themeConfig.navbarType || 'floating',
      navMenuItems,
      routerTransition: themeConfig.routerTransition || 'none',
      routeTitle: this.$route.meta.pageTitle,
      routeTitleParams: this.$route.meta.pageTitleParams
    };
  },
  watch: {
    '$route' () {
      this.routeTitle = this.$route.meta.pageTitle;
      this.routeTitleParams = this.$route.meta.pageTitleParams;
    },
    isThemeDark (val) {
      const color = this.navbarColor === '#fff' && val ? '#10163a' : '#fff';
      this.updateNavbarColor(color);
    },
    '$store.state.mainLayoutType' (val) {
      this.setNavMenuVisibility(val);
    }
  },
  computed: {
    maintenance () {
      return this.$store.state.maintenance.maintenance;
    },
    filteredGlobalAction () {
      return this.$route.meta.globalActions ? this.$route.meta.globalActions.filter(action => action.active === undefined || !!action.active) : [];
    },
    bodyOverlay () {
      return this.$store.state.bodyOverlay;
    },
    contentAreaClass () {
      if (this.mainLayoutType === 'vertical') {
        if (this.verticalNavMenuWidth === 'default') return 'content-area-reduced';
        else if (this.verticalNavMenuWidth === 'reduced') return 'content-area-lg';
        else return 'content-area-full';
      } else return 'content-area-full';
    },
    footerClasses () {
      return {
        'footer-hidden': this.footerType === 'hidden',
        'footer-sticky': this.footerType === 'sticky',
        'footer-static': this.footerType === 'static'
      };
    },
    isAppPage () {
      return this.$route.meta.no_scroll;
    },
    isThemeDark () {
      return this.$store.state.theme === 'dark';
    },
    layoutTypeClass () {
      return `main-${this.mainLayoutType}`;
    },
    mainLayoutType () {
      return this.$store.state.mainLayoutType;
    },
    navbarClasses () {
      return {
        'navbar-hidden': this.navbarType === 'hidden',
        'navbar-sticky': this.navbarType === 'sticky',
        'navbar-static': this.navbarType === 'static',
        'navbar-floating': this.navbarType === 'floating'
      };
    },
    verticalNavMenuWidth () {
      return this.$store.state.verticalNavMenuWidth;
    },
    windowWidth () {
      return this.$store.state.windowWidth;
    }
  },
  methods: {
    changeRouteTitle (title) {
      this.routeTitle = title;
    },
    updateNavbarColor (val) {
      this.navbarColor = val;
      if (val === '#fff') this.isNavbarDark = false;
      else this.isNavbarDark = true;
    },
    setNavMenuVisibility (layoutType) {
      if ((layoutType === 'horizontal' && this.windowWidth >= 1200) || (layoutType === 'vertical' && this.windowWidth < 1200)) {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false);
        this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu');
      } else {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true);
      }
    }
  },
  created () {
    const color = this.navbarColor === '#fff' && this.isThemeDark ? '#10163a' : this.navbarColor;
    this.updateNavbarColor(color);
    this.setNavMenuVisibility(this.$store.state.mainLayoutType);
  }
};

</script>

